import { useEffect, useRef, useState } from "react";
import useMicSettings from "zustand-store/mic";
import { ReactComponent as MicIcon } from "assets/svgs/mic.svg";
import { WORKLETCODE_PLACEHOLDER } from "enums";

const COUNTDOWN_SECONDS = 7;
const WS_URL = `${process.env.REACT_APP_DEEPGRAM_WS_URL}/ws/live_transcribe`;

function getCognitoIdToken() {
  const key = Object.keys(localStorage).find(
    (k) =>
      k.startsWith("CognitoIdentityServiceProvider.") && k.endsWith(".idToken")
  );
  return key ? localStorage.getItem(key) : null;
}

const MicTest = ({ className }) => {
  const { inputDevice } = useMicSettings();

  const [phase, setPhase] = useState("idle"); // 'idle' | 'recording' | 'done' | 'error'
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const [transcript, setTranscript] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const wsRef = useRef(null);
  const audioContextRef = useRef(null);
  const workletNodeRef = useRef(null);
  const streamRef = useRef(null);
  const countdownRef = useRef(null);
  const transcriptRef = useRef("");

  // Waveform
  const canvasRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const animFrameRef = useRef(null);

  const startDrawing = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const canvasCtx = canvas.getContext("2d");

    const drawBars = () => {
      if (!analyserRef.current || !dataArrayRef.current || !canvasRef.current) return;

      analyserRef.current.getByteFrequencyData(dataArrayRef.current);

      const w = canvas.width;
      const h = canvas.height;
      const cx = w / 2;
      const cy = h / 2;

      canvasCtx.clearRect(0, 0, w, h);

      const numBars = 18;
      const barW = 3;
      const gap = 2;
      const step = barW + gap;

      const drawPill = (x, y, bw, bh) => {
        const r = Math.min(bw / 2, bh / 2);
        canvasCtx.beginPath();
        canvasCtx.moveTo(x + r, y);
        canvasCtx.lineTo(x + bw - r, y);
        canvasCtx.arcTo(x + bw, y, x + bw, y + r, r);
        canvasCtx.lineTo(x + bw, y + bh - r);
        canvasCtx.arcTo(x + bw, y + bh, x + bw - r, y + bh, r);
        canvasCtx.lineTo(x + r, y + bh);
        canvasCtx.arcTo(x, y + bh, x, y + bh - r, r);
        canvasCtx.lineTo(x, y + r);
        canvasCtx.arcTo(x, y, x + r, y, r);
        canvasCtx.closePath();
        canvasCtx.fill();
      };

      for (let i = 0; i < numBars; i++) {
        const binIndex = Math.floor(
          (i / numBars) * (dataArrayRef.current.length / 4)
        );
        const amplitude = dataArrayRef.current[binIndex] / 255;
        const barH = Math.max(4, amplitude * (h - 6));
        const alpha = Math.max(0.2, 0.95 - (i / numBars) * 0.75);

        canvasCtx.fillStyle = `rgba(51, 187, 207, ${alpha})`;

        const barY = cy - barH / 2;
        drawPill(cx + i * step + 1, barY, barW, barH);
        drawPill(cx - (i + 1) * step, barY, barW, barH);
      }

      animFrameRef.current = requestAnimationFrame(drawBars);
    };

    drawBars();
  };

  const cleanup = () => {
    clearInterval(countdownRef.current);

    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    analyserRef.current = null;
    dataArrayRef.current = null;

    const canvas = canvasRef.current;
    if (canvas) canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);

    wsRef.current?.close();
    wsRef.current = null;

    workletNodeRef.current?.disconnect();
    workletNodeRef.current = null;

    if (audioContextRef.current) {
      if (audioContextRef.current.state !== "closed") {
        audioContextRef.current.close();
      }
      audioContextRef.current = null;
    }

    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  // Cleanup on unmount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => cleanup(), []);

  // Cancel if device changes mid-recording
  useEffect(() => {
    if (phase === "recording") {
      cleanup();
      setPhase("idle");
      setCountdown(COUNTDOWN_SECONDS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputDevice?.deviceId]);

  const setupAudioProcessing = async (stream, ws) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)(
        { sampleRate: 16000 }
      );
      audioContextRef.current = audioContext;

      // Analyser for waveform visualization
      const analyserNode = audioContext.createAnalyser();
      analyserNode.fftSize = 256;
      analyserRef.current = analyserNode;
      dataArrayRef.current = new Uint8Array(analyserNode.frequencyBinCount);

      const blob = new Blob([WORKLETCODE_PLACEHOLDER], {
        type: "application/javascript",
      });
      const workletUrl = URL.createObjectURL(blob);
      await audioContext.audioWorklet.addModule(workletUrl);

      const source = audioContext.createMediaStreamSource(stream);
      const workletNode = new AudioWorkletNode(audioContext, "pcm-processor");
      workletNodeRef.current = workletNode;

      workletNode.port.onmessage = (event) => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(event.data);
        }
      };

      // Fan out: source → analyser (visualization) + source → worklet (PCM stream)
      source.connect(analyserNode);
      source.connect(workletNode);
      workletNode.connect(audioContext.destination);

      startDrawing();
    } catch (err) {
      console.error("Audio processing setup failed:", err);
      cleanup();
      setErrorMsg("Audio processing failed. Please try again.");
      setPhase("error");
    }
  };

  const handleStart = async () => {
    setPhase("recording");
    setCountdown(COUNTDOWN_SECONDS);
    setTranscript("");
    setErrorMsg("");
    transcriptRef.current = "";

    try {
      const constraints = inputDevice?.deviceId
        ? { audio: { deviceId: { exact: inputDevice.deviceId } } }
        : { audio: true };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      const idToken = getCognitoIdToken();
      const ws = new WebSocket(`${WS_URL}?token=${idToken}`);
      ws.binaryType = "arraybuffer";
      wsRef.current = ws;

      ws.onopen = () => setupAudioProcessing(stream, ws);

      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data.type === "interim" && data.text) {
            transcriptRef.current =
              `${transcriptRef.current} ${data.text}`.trim();
            setTranscript(transcriptRef.current);
          }
        } catch {}
      };

      ws.onerror = () => {
        cleanup();
        setErrorMsg("Connection failed. Please check your internet and try again.");
        setPhase("error");
      };

      let remaining = COUNTDOWN_SECONDS;
      countdownRef.current = setInterval(() => {
        remaining -= 1;
        setCountdown(remaining);
        if (remaining === 0) {
          cleanup();
          setPhase("done");
        }
      }, 1000);
    } catch (err) {
      console.error("Mic test failed:", err);
      setErrorMsg(
        err.name === "NotAllowedError"
          ? "Microphone access denied. Please allow microphone permissions and try again."
          : "Could not access microphone. Please check your settings and try again."
      );
      setPhase("error");
    }
  };

  const handleReset = () => {
    setPhase("idle");
    setCountdown(COUNTDOWN_SECONDS);
    setTranscript("");
    setErrorMsg("");
    transcriptRef.current = "";
  };

  const isIdle = phase === "idle";
  const isActive = phase === "recording";
  const isDone = phase === "done";
  const isError = phase === "error";

  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      {/* Controls row */}
      <div className="flex flex-row items-center gap-4 flex-wrap">
        {/* Idle / Done / Error: action button */}
        {(isIdle || isDone || isError) && (
          <button
            onClick={isIdle ? handleStart : handleReset}
            className="flex items-center gap-2 text-[13px] font-semibold text-brand-purple bg-purple-50 border border-brand-purple/30 rounded-full px-4 py-2 hover:bg-purple-100 transition-colors"
          >
            <MicIcon />
            {isIdle ? "Start Transcribing" : "Try Again"}
          </button>
        )}

        {/* Recording: countdown ring + waveform */}
        {isActive && (
          <div className="flex items-center gap-3">
            <div className="relative w-12 h-12 flex items-center justify-center">
              <span className="absolute inset-0 rounded-full bg-primary opacity-30 animate-ping" />
              <span className="relative w-12 h-12 rounded-full bg-primary flex items-center justify-center text-xl font-bold text-black">
                {countdown}
              </span>
            </div>
            <canvas ref={canvasRef} width={200} height={50} />
            <span className="text-sm text-gray-500 font-medium">Recording…</span>
          </div>
        )}
      </div>

      {/* Error message — sits above the fixed-height transcript box */}
      {isError && errorMsg && (
        <p className="text-sm text-red-500 font-medium">{errorMsg}</p>
      )}

      {/* Transcript box — always rendered so height stays fixed */}
      <div className="flex flex-col gap-1 w-full">
        <p className="text-sm font-semibold text-gray-700">Transcript</p>
        <textarea
          readOnly
          value={transcript}
          placeholder={
            isIdle
              ? "Press 'Start Transcribing' to test your microphone…"
              : isActive
              ? "Transcribing your audio live…"
              : ""
          }
          className="w-full h-36 p-3 border border-gray-300 rounded-xl bg-gray-50 text-sm text-gray-800 resize-none focus:outline-none cursor-default"
        />
      </div>
    </div>
  );
};

export default MicTest;
